import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BookOpen, Mail, Check, Lock } from "lucide-react";

const BREVO_ACTION =
  "https://ff1a1a3d.sibforms.com/serve/MUIFAJtX0E-1vlekpWRTA6JQPC0SyNQDJ-AiB0OpU9K1NCNxPpe8daYyWfmM6uMlBzZNlmeakGAiZsjK-QVbvLOqrJOuwtNfmapB2--8xITS627a9jayshPZ6ipeoi4vorAyLBGuxBsHY9xzkvYxJ2B_IEUqWBTf4DNQ199PJyuEXMingDJecIqRJAmtdAZtXrkvV1ADPiBIFtAoNg==";

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export default function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const body = new URLSearchParams();
      body.append("EMAIL", email);
      body.append("email_address_check", "");
      body.append("locale", "en");
      // no-cors: the browser sends the request to Brevo (opaque response).
      await fetch(BREVO_ACTION, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", { method: "brevo_free_ebook" });
      }
      setSubmitted(true);
      toast.success("Almost there! Check your inbox to confirm and unlock your free ebook.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="newsletter-section"
      className="relative py-24 md:py-32 bg-[#0A0A0A] overflow-hidden"
    >
      {/* Ambient gold glow */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-[#D4AF37]/8 blur-[130px] rounded-full pointer-events-none" />

      <div className="relative z-10 max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="relative border border-[#D4AF37]/30 bg-[#111111] p-8 md:p-14 text-center"
        >
          {/* Free badge */}
          <div className="inline-flex items-center gap-2 border border-[#D4AF37]/50 bg-[#D4AF37]/10 px-5 py-2 mb-8">
            <BookOpen className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">
              Free ebook — instant download
            </span>
          </div>

          {!submitted ? (
            <>
              <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
                Get Your <span className="gold-shimmer">Free Faceless Content</span> Ebook
              </h2>
              <p className="mt-6 text-lg text-zinc-400 max-w-xl mx-auto">
                Want to make money on social media without a large following? Join the 
                insider list and instantly unlock our winning ebook product — plus first 
                access to new drops, bonuses, and exclusive discounts. No spam, unsubscribe anytime.
              </p>

              <form
                onSubmit={handleSubmit}
                className="mt-10 flex flex-col sm:flex-row items-stretch gap-3 max-w-lg mx-auto"
                data-testid="newsletter-form"
              >
                <div className="relative flex-1">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input
                    type="email"
                    name="EMAIL"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    autoComplete="email"
                    required
                    data-testid="newsletter-email-input"
                    className="w-full bg-[#0A0A0A] border border-zinc-700 focus:border-[#D4AF37] outline-none text-white placeholder-zinc-500 pl-12 pr-4 py-4 text-base transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="newsletter-submit-button"
                  className="btn-gold px-8 py-4 text-base whitespace-nowrap"
                >
                  {loading ? "Sending…" : "Send My Ebook"}
                </button>
              </form>

              <div className="mt-6 flex items-center justify-center gap-2 text-xs text-zinc-500">
                <Lock className="w-3.5 h-3.5" />
                Your email is safe. We'll never share or sell it.
              </div>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              data-testid="newsletter-success"
              className="py-6"
            >
              <div className="w-16 h-16 mx-auto flex items-center justify-center border border-[#D4AF37]/40 bg-[#D4AF37]/5">
                <Check className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold mt-6">
                One last step — confirm your email
              </h2>
              <p className="mt-4 text-lg text-zinc-400 max-w-lg mx-auto">
                We just sent you a confirmation email. Click the link inside to verify your
                subscription and instantly download your free faceless content ebook. (Don't
                forget to check your spam folder.)
              </p>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
